import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat.js';
import 'dayjs/locale/fr.js';
import 'dayjs/locale/es.js';
import fr from './assets/i18n/fr.json';
import en from './assets/i18n/en.json';
import es from './assets/i18n/es.json';

export const SUPPORTED_LOCALES = ['fr', 'en', 'es'] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];
export const DEFAULT_LOCALE: SupportedLocale = 'fr';

/** The active language as a supported locale; an unknown one falls back to the default. */
export const activeLocale = (language = i18n.resolvedLanguage): SupportedLocale =>
  SUPPORTED_LOCALES.find((locale) => locale === language) ?? DEFAULT_LOCALE;

dayjs.extend(localizedFormat);

// In test mode only, fail loudly on any key that has no entry in the JSON
// resources, so an un-translated `t('Key')` breaks the suite instead of silently
// falling back. Production behaviour is unchanged (silent fallback to `fr`).
const isTest = process.env.NODE_ENV === 'test';

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      fr: { translation: fr },
      en: { translation: en },
      es: { translation: es },
    },
    fallbackLng: DEFAULT_LOCALE,
    supportedLngs: [...SUPPORTED_LOCALES],
    detection: { order: ['localStorage'], lookupLocalStorage: 'lang', caches: [] },
    interpolation: { escapeValue: false },
    // Keys are full English source strings, so they routinely contain `:` and
    // `.` (e.g. "Email:", "An error occurred.", "1. Information We Collect").
    // i18next's default separators would parse those as namespace / nested-key
    // boundaries and resolve to the wrong (often empty) key; disabling both makes
    // every literal key resolve as-is.
    nsSeparator: false,
    keySeparator: false,
    ...(isTest
      ? {
          saveMissing: true,
          missingKeyHandler: (_lngs: readonly string[], _ns: string, key: string) => {
            throw new Error(`Missing i18n key: "${key}"`);
          },
        }
      : {}),
  });

const applyLocale = (lng: string) => {
  dayjs.locale(lng);
  // Real page language: else Chrome translates the UI "from English" and React crashes on its DOM edits
  document.documentElement.lang = activeLocale(lng);
};
applyLocale(i18n.language);
i18n.on('languageChanged', applyLocale);

export default i18n;
